import { del, get, keys, set, type UseStore } from 'idb-keyval';

import { DEFAULT_MAX_RETRIES, IDB_DATABASE_NAME, STORAGE_PREFIXES } from '../constants.js';
import { getActiveIdentity } from '../identity.js';
import type { MutationMethod, MutationQueueItem, MutationQueueStatus } from '../types.js';

import { createPortalStore } from './idbStore.js';

/**
 * Options for creating a mutation queue
 */
export interface MutationQueueOptions {
    /** Portal name for namespace isolation */
    portalName: string;
    /** Maximum number of queued mutations (default: 100) */
    maxQueueSize?: number;
    /** Maximum retry attempts per mutation */
    maxRetries?: number;
}

/**
 * Creates a mutation queue manager for offline mutations
 */
export function createMutationQueue(options: MutationQueueOptions) {
    const { portalName, maxQueueSize = 100, maxRetries = DEFAULT_MAX_RETRIES } = options;

    const store: UseStore = createPortalStore(`${IDB_DATABASE_NAME}-${portalName}`, 'mutation-queue');

    /**
     * Generates a unique mutation ID
     */
    function generateId(): string {
        return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }

    /**
     * Per-identity prefix for this queue's keys.
     *
     * Scoping by the active identity keeps a mutation queued as one user from
     * being synced as another when the same portal+browser switches effective
     * users (impersonation, logout/login). Each identity flushes only its own
     * queued mutations.
     */
    function keyPrefix(): string {
        return `${getActiveIdentity()}:${STORAGE_PREFIXES.MUTATION_QUEUE}:`;
    }

    /**
     * Creates a storage key for a mutation
     */
    function createKey(id: string): string {
        return `${keyPrefix()}${id}`;
    }

    /**
     * Enqueues a new mutation
     */
    async function enqueue<TVariables>(
        resourceKey: string,
        method: MutationMethod,
        variables: TVariables,
        options?: {
            optimisticData?: unknown;
            rollbackData?: unknown;
            resourceVersion?: number;
        },
    ): Promise<MutationQueueItem<TVariables>> {
        // Check queue size
        const currentSize = await getQueueSize();
        if (currentSize >= maxQueueSize) {
            throw new Error(`Mutation queue is full (max: ${maxQueueSize})`);
        }

        const id = generateId();
        const item: MutationQueueItem<TVariables> = {
            id,
            timestamp: Date.now(),
            resourceKey,
            method,
            variables,
            status: 'pending',
            retryCount: 0,
            optimisticData: options?.optimisticData,
            rollbackData: options?.rollbackData,
            resourceVersion: options?.resourceVersion,
        };

        await set(createKey(id), item, store);
        return item;
    }

    /**
     * Gets a mutation by ID
     */
    async function getById<TVariables = unknown>(id: string): Promise<MutationQueueItem<TVariables> | null> {
        try {
            const item = await get<MutationQueueItem<TVariables>>(createKey(id), store);
            return item ?? null;
        } catch (error) {
            console.warn(`[MutationQueue] Failed to get mutation ${id}:`, error);
            return null;
        }
    }

    /**
     * Gets all mutations in the queue
     */
    async function getAll<TVariables = unknown>(): Promise<MutationQueueItem<TVariables>[]> {
        try {
            const allKeys = await keys<string>(store);
            const mutationKeys = allKeys.filter((k) => k.startsWith(keyPrefix()));

            const items: MutationQueueItem<TVariables>[] = [];
            for (const key of mutationKeys) {
                const item = await get<MutationQueueItem<TVariables>>(key, store);
                if (item) items.push(item);
            }

            // Sort by timestamp (oldest first)
            return items.sort((a, b) => a.timestamp - b.timestamp);
        } catch (error) {
            console.warn('[MutationQueue] Failed to get all mutations:', error);
            return [];
        }
    }

    /**
     * Gets mutations by status
     */
    async function getByStatus<TVariables = unknown>(status: MutationQueueStatus): Promise<MutationQueueItem<TVariables>[]> {
        const all = await getAll<TVariables>();
        return all.filter((item) => item.status === status);
    }

    /**
     * Gets pending mutations (ready to be processed)
     */
    async function getPending<TVariables = unknown>(): Promise<MutationQueueItem<TVariables>[]> {
        const all = await getAll<TVariables>();
        return all.filter((item) => item.status === 'pending' || item.status === 'retrying');
    }

    /**
     * Gets the next mutation to process
     */
    async function peek<TVariables = unknown>(): Promise<MutationQueueItem<TVariables> | null> {
        const pending = await getPending<TVariables>();
        return pending[0] ?? null;
    }

    /**
     * Updates a mutation's status
     */
    async function updateStatus(id: string, status: MutationQueueStatus, error?: string): Promise<void> {
        const item = await getById(id);
        if (!item) return;

        const updated: MutationQueueItem = {
            ...item,
            status,
            lastError: error,
            retryCount: status === 'retrying' ? item.retryCount + 1 : item.retryCount,
        };

        await set(createKey(id), updated, store);
    }

    /**
     * Marks a mutation as processing
     */
    async function markProcessing(id: string): Promise<void> {
        await updateStatus(id, 'processing');
    }

    /**
     * Marks a mutation for retry
     */
    async function markRetry(id: string, error: string): Promise<boolean> {
        const item = await getById(id);
        if (!item) return false;

        if (item.retryCount >= maxRetries) {
            await updateStatus(id, 'failed', error);
            return false;
        }

        await updateStatus(id, 'retrying', error);
        return true;
    }

    /**
     * Marks a mutation as having a conflict
     */
    async function markConflict(id: string): Promise<void> {
        await updateStatus(id, 'conflict');
    }

    /**
     * Marks a mutation as failed
     */
    async function markFailed(id: string, error: string): Promise<void> {
        await updateStatus(id, 'failed', error);
    }

    /**
     * Removes a mutation from the queue
     */
    async function remove(id: string): Promise<void> {
        try {
            await del(createKey(id), store);
        } catch (error) {
            console.warn(`[MutationQueue] Failed to remove mutation ${id}:`, error);
        }
    }

    /**
     * Clears all mutations from the queue
     */
    async function clearAll(): Promise<void> {
        try {
            const allKeys = await keys<string>(store);
            const mutationKeys = allKeys.filter((k) => k.startsWith(keyPrefix()));
            for (const key of mutationKeys) {
                await del(key, store);
            }
        } catch (error) {
            console.warn('[MutationQueue] Failed to clear queue:', error);
        }
    }

    /**
     * Clears completed mutations (processed successfully)
     */
    async function clearCompleted(): Promise<number> {
        // Note: We don't have a 'completed' status - successful mutations are removed
        // This function clears failed mutations that have been acknowledged
        return 0;
    }

    /**
     * Clears failed mutations
     */
    async function clearFailed(): Promise<number> {
        const failed = await getByStatus('failed');
        for (const item of failed) {
            await remove(item.id);
        }
        return failed.length;
    }

    /**
     * Gets the current queue size
     */
    async function getQueueSize(): Promise<number> {
        const all = await getAll();
        return all.length;
    }

    /**
     * Gets queue statistics
     */
    async function getStats(): Promise<{
        total: number;
        pending: number;
        processing: number;
        retrying: number;
        failed: number;
        conflict: number;
    }> {
        const all = await getAll();
        return {
            total: all.length,
            pending: all.filter((i) => i.status === 'pending').length,
            processing: all.filter((i) => i.status === 'processing').length,
            retrying: all.filter((i) => i.status === 'retrying').length,
            failed: all.filter((i) => i.status === 'failed').length,
            conflict: all.filter((i) => i.status === 'conflict').length,
        };
    }

    /**
     * Resets a failed mutation to pending status for retry
     */
    async function resetToPending(id: string): Promise<void> {
        const item = await getById(id);
        if (!item) return;

        const updated: MutationQueueItem = {
            ...item,
            status: 'pending',
            retryCount: 0,
            lastError: undefined,
        };

        await set(createKey(id), updated, store);
    }

    /**
     * Gets mutations for a specific resource
     */
    async function getByResourceKey<TVariables = unknown>(resourceKey: string): Promise<MutationQueueItem<TVariables>[]> {
        const all = await getAll<TVariables>();
        return all.filter((item) => item.resourceKey === resourceKey);
    }

    return {
        enqueue,
        getById,
        getAll,
        getByStatus,
        getPending,
        peek,
        updateStatus,
        markProcessing,
        markRetry,
        markConflict,
        markFailed,
        remove,
        clearAll,
        clearCompleted,
        clearFailed,
        getQueueSize,
        getStats,
        resetToPending,
        getByResourceKey,
        portalName,
        store,
    };
}

export type MutationQueue = ReturnType<typeof createMutationQueue>;
